import { useFamilyLetters } from '@app/lib'
import { useSugarContext } from '@app/providers'
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  createStyles,
  Group,
  LoadingOverlay,
  MantineColor,
  Select,
  Stack,
  type StackProps,
  Table,
  Text
} from '@mantine/core'
import { IconAlertCircle, IconDownload } from '@tabler/icons-react'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import { find, first, map } from 'lodash'
import { useRouter } from 'next/router'
import { type FC, useCallback, useMemo } from 'react'

dayjs.extend(customParseFormat)
dayjs.extend(localizedFormat)

// Sugar emits letterDate as `YYYY-MM-DD HH:MM:SS` (server tz), NOT ISO 8601 — see MIQ-2393
// OpenAPI. Parse with the explicit format so Safari/strict parsers don't return Invalid Date.
const SUGAR_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'

// Letter-type values are emitted verbatim from Sugar `documents.template_type`. The set is
// open by design (see MIQ-2393 OpenAPI) — we map known keys to friendly labels and pass the
// raw key through verbatim for anything new, so support can correlate unknown templates.
const LETTER_TYPE_LABELS: Record<string, string> = {
  approved: 'Approval',
  approved_spanish: 'Approval (Spanish)',
  pending: 'Pending',
  pending_spanish: 'Pending (Spanish)',
  denied: 'Denial',
  denied_spanish: 'Denial (Spanish)',
  denied_3yo: 'Denial (3yo)',
  waitlist: 'Waitlist',
  waitlist_3yo: 'Waitlist (3yo)',
  gap_scholarship_agreement: 'DPP Scholarship Agreement',
  gap_scholarship_agreement_spanish: 'DPP Scholarship Agreement (Spanish)'
}

const getLetterTypeColor = (type: string): MantineColor => {
  if (type.startsWith('approved')) return 'green'
  if (type.startsWith('pending')) return 'yellow'
  if (type.startsWith('denied')) return 'red'
  if (type.startsWith('waitlist')) return 'orange'
  if (type.startsWith('gap_scholarship')) return 'blue'
  return 'gray'
}

const getLetterTypeLabel = (type: string): string => LETTER_TYPE_LABELS[type] ?? type

const useStyles = createStyles(() => ({
  actionColumn: {
    textAlign: 'center',
    verticalAlign: 'middle',
    width: 64
  },
  fitWidth: {
    whiteSpace: 'nowrap',
    width: 1
  }
}))

export interface FamilyLettersTableProps extends Omit<StackProps, 'children'> {}

export const FamilyLettersTable: FC<FamilyLettersTableProps> = (props) => {
  const { classes } = useStyles()
  const router = useRouter()
  const { query: routerQuery, replace: routerReplace } = router
  const { lettersProgramYears } = useSugarContext()

  // programYearId is optional per MIQ-2393. When ?programYear is present AND maps to
  // a letters-eligible PY for the active site, use it. Otherwise (URL absent, or set
  // by a sibling Documents tab whose PY filter differs — KB writes roster PYs to the
  // same query param), fall back to the first letters-eligible PY for display only.
  // We don't rewrite the URL: leaving it alone preserves the originating tab's
  // selection on round-trip (e.g. KB → Letters → KB keeps the KB selection).
  const urlProgramYear = typeof routerQuery.programYear === 'string' ? routerQuery.programYear : null
  const matchedLettersPY = urlProgramYear !== null ? find(lettersProgramYears, ['id', urlProgramYear]) : undefined
  const programYearId = matchedLettersPY?.id ?? first(lettersProgramYears)?.id ?? null

  const { data: letters, isError, isFetching, refetch } = useFamilyLetters({ programYearId })

  const setProgramYearId = useCallback(
    (next: string | null) => {
      if (!next || next === programYearId) return

      void routerReplace({ pathname: router.pathname, query: { ...routerQuery, programYear: next } }, undefined, {
        shallow: true
      })
    },
    [programYearId, router.pathname, routerQuery, routerReplace]
  )

  const programYearOptions = useMemo(
    () =>
      map(lettersProgramYears, (py) => ({
        label: py.name,
        value: py.id
      })),
    [lettersProgramYears]
  )

  return (
    <Stack
      pos='relative'
      spacing='lg'
      {...props}
    >
      <LoadingOverlay visible={isFetching} />
      {lettersProgramYears.length > 0 && (
        <Group
          align='center'
          grow
          position='center'
        >
          <Select
            data={programYearOptions}
            label='Program Year'
            onChange={setProgramYearId}
            value={programYearId}
            variant='default'
          />
        </Group>
      )}
      {isError ? (
        <Alert
          color='red'
          icon={<IconAlertCircle size={16} />}
          title='Could not load family letters'
        >
          <Stack spacing='sm'>
            <Text size='sm'>Something went wrong loading the family letters listing. Please try again.</Text>
            <Box>
              <Button
                color='red'
                onClick={() => refetch()}
                size='xs'
                variant='light'
              >
                Retry
              </Button>
            </Box>
          </Stack>
        </Alert>
      ) : (
        <Table
          horizontalSpacing='xs'
          verticalSpacing='xs'
          withBorder
        >
          <thead>
            <tr>
              <th>
                <Text
                  color='black'
                  fw='600'
                >
                  Child Name
                </Text>
              </th>
              <th className={classes.fitWidth}>
                <Text
                  color='black'
                  fw='600'
                >
                  Letter Type
                </Text>
              </th>
              <th className={classes.fitWidth}>
                <Text
                  color='black'
                  fw='600'
                >
                  Letter Date
                </Text>
              </th>
              <th className={classes.actionColumn}>
                <Text
                  color='black'
                  fw='600'
                >
                  Download
                </Text>
              </th>
            </tr>
          </thead>
          <tbody>
            {letters?.data.length === 0 && (
              <tr>
                <td colSpan={4}>
                  <Text
                    align='center'
                    color='dimmed'
                    py='md'
                  >
                    No family letters for this program year yet.
                  </Text>
                </td>
              </tr>
            )}
            {map(letters?.data, (letter) => (
              <tr key={`letter-${letter.downloadId}`}>
                <td>{`${letter.childFirstName} ${letter.childLastName}`}</td>
                <td className={classes.fitWidth}>
                  <Badge
                    color={getLetterTypeColor(letter.letterType)}
                    variant='filled'
                  >
                    {getLetterTypeLabel(letter.letterType)}
                  </Badge>
                </td>
                <td className={classes.fitWidth}>
                  {letter.letterDate ? dayjs(letter.letterDate, SUGAR_DATETIME_FORMAT).format('LL') : '—'}
                </td>
                <td className={classes.actionColumn}>
                  <ActionIcon
                    color='brand-red'
                    component='a'
                    href={`/api/letters/${letter.downloadId}`}
                    tabIndex={0}
                    title='Download Family Letter'
                    variant='transparent'
                  >
                    <IconDownload size={16} />
                  </ActionIcon>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Stack>
  )
}
